import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Layout } from "@/components/Layout";
import { LiveBadge } from "@/components/pakistan/LiveBadge";
import { PakistanNavigator, PakistanBreadcrumbSchema } from "@/components/pakistan/PakistanNavigator";
import { FaqSection } from "@/components/FaqSection";
import {
  karatPurity,
  karatLabel,
  KARATS,
  GRAMS_PER_TOLA,
  formatMoney,
  type Karat,
} from "@/lib/gold-data";
import { goldRatesQueryOptions, useGoldRates } from "@/lib/nonblocking-rates";

/**
 * /pakistan/calculator — Interactive live PKR gold-value calculator.
 * Karat × weight × unit → today's PKR value, plus making-charge and
 * GST estimator. Recalculated on every keystroke against the live spot
 * price + USD/PKR rate.
 */

const UNIT_GRAMS = {
  tola: GRAMS_PER_TOLA,
  gram: 1,
  "10-gram": 10,
  ratti: 0.121,
  masha: 0.972,
} as const;
type UnitSlug = keyof typeof UNIT_GRAMS;

const UNIT_LABEL: Record<UnitSlug, string> = {
  tola: "Tola",
  gram: "Gram",
  "10-gram": "10 Gram",
  ratti: "Ratti",
  masha: "Masha",
};

const CANONICAL = "https://drhint.com/pakistan/calculator";

export const Route = createFileRoute("/pakistan/calculator")({
  head: () => ({
    meta: [
      { title: "Gold Price Calculator Pakistan — Live PKR Value (Tola / Gram / Ratti)" },
      {
        name: "description",
        content:
          "Live gold price calculator for Pakistan. Enter karat, weight and unit (tola, gram, 10 gram, ratti, masha) to see today's PKR value with making charge & 17% GST estimate.",
      },
      { property: "og:title", content: "Gold Price Calculator Pakistan — Live PKR Value" },
      {
        property: "og:description",
        content:
          "Live tola/gram/ratti gold value in PKR. Karat converter with making charge and GST estimator.",
      },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
  },
  component: PakistanCalculator,
});

function PakistanCalculator() {
  const { data } = useGoldRates();
  const [karat, setKarat] = useState<Karat>("22k");
  const [weight, setWeight] = useState<string>("1");
  const [unit, setUnit] = useState<UnitSlug>("tola");
  const [makingPct, setMakingPct] = useState<string>("12");
  const [includeGst, setIncludeGst] = useState<boolean>(false);

  const perGram24Pkr = useMemo(
    () => (data.spotUsdPerOz / 31.1035) * data.pkrPerUsd,
    [data],
  );

  const parsed = Math.max(0, Number(weight) || 0);
  const grams = parsed * UNIT_GRAMS[unit];
  const purity = karatPurity(karat);
  const goldValue = perGram24Pkr * purity * grams;
  const makingCharge = goldValue * ((Number(makingPct) || 0) / 100);
  const subtotal = goldValue + makingCharge;
  const gst = includeGst ? subtotal * 0.17 : 0;
  const total = subtotal + gst;

  const faqs = [
    {
      q: "Ye gold calculator kaise kaam karta hai?",
      a: `Aapka input weight (${UNIT_LABEL[unit]}) grams mein convert hota hai, phir 24K per-gram PKR rate se multiply hota hai. Karat purity apply hoti hai (${karatLabel(karat)} = ${Math.round(purity * 1000) / 10}%). Rate live spot + USD/PKR se every 10 min update hota hai.`,
    },
    {
      q: "Making charge kya hoti hai?",
      a: "Jeweller ki labour + design fee, generally 8–20% of gold value. Machine-made lightweight items par 8–10%, hand-crafted heavy jewellery par 15–20%. Bridal / kundan work par 25%+ bhi ho sakti hai. Calculator mein default 12% set hai.",
    },
    {
      q: "17% GST kab lagti hai?",
      a: "Retail jewellery invoices par SRO-notified 17% sales tax applicable hoti hai. Bullion (bars/coins) aur old gold exchange par different treatment hoti hai. Apna invoice hamesha check karen — GST separately dikhni chahiye.",
    },
    {
      q: "Ratti aur masha ka conversion?",
      a: "1 tola = 12 masha = 96 ratti. Metric mein: 1 masha ≈ 0.972 g, 1 ratti ≈ 0.121 g. Chhoti items — nose pin, ear tops — masha/ratti mein weigh hoti hain.",
    },
    {
      q: "Calculator ka result actual jeweller rate se match hoga?",
      a: "Base gold value 100% accurate hai (international spot + live USD/PKR). Making charge aur GST aapke jeweller ke policy par depend karti hain — variation ke liye field editable hai. Local sarafa bazaar 0.1–0.2% premium/discount ho sakta hai.",
    },
  ];

  const calcSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Pakistan Gold Price Calculator",
    applicationCategory: "FinanceApplication",
    operatingSystem: "Any",
    url: CANONICAL,
    offers: { "@type": "Offer", price: 0, priceCurrency: "PKR" },
  };

  return (
    <Layout>
      <section className="mx-auto max-w-3xl px-4 py-8">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(calcSchema) }}
        />
        <PakistanBreadcrumbSchema
          items={[
            { name: "Pakistan", href: "https://drhint.com/pakistan" },
            { name: "Calculator", href: CANONICAL },
          ]}
        />

        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <Link to="/pakistan" className="hover:underline">Pakistan</Link> › Calculator
        </nav>

        <h1 className="text-3xl md:text-4xl font-bold">
          Gold Price Calculator Pakistan
        </h1>
        <p className="mt-2 text-muted-foreground">
          Live PKR value of any gold quantity — enter karat, weight and unit.
          Making charge + 17% GST estimator built-in.
        </p>
        <div className="mt-3">
          <LiveBadge />
        </div>

        <div className="mt-6 rounded-2xl border border-primary/30 bg-card p-5 shadow-sm">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Karat (purity)</span>
              <select
                value={karat}
                onChange={(e) => setKarat(e.target.value as Karat)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2"
              >
                {KARATS.map((k) => (
                  <option key={k} value={k}>
                    {karatLabel(k)} ({Math.round(karatPurity(k) * 1000) / 10}%)
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-sm">
              <span className="mb-1 block font-medium">Unit</span>
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value as UnitSlug)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2"
              >
                {(Object.keys(UNIT_GRAMS) as UnitSlug[]).map((u) => (
                  <option key={u} value={u}>
                    {UNIT_LABEL[u]} ({UNIT_GRAMS[u]} g)
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-sm">
              <span className="mb-1 block font-medium">Weight</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.001"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 tabular-nums"
              />
            </label>

            <label className="block text-sm">
              <span className="mb-1 block font-medium">Making charge %</span>
              <input
                type="number"
                min="0"
                max="50"
                step="0.5"
                value={makingPct}
                onChange={(e) => setMakingPct(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 tabular-nums"
              />
            </label>
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeGst}
              onChange={(e) => setIncludeGst(e.target.checked)}
              className="h-4 w-4"
            />
            Include 17% GST (retail jewellery invoice)
          </label>

          <div className="mt-5 rounded-xl border border-border bg-muted/40 p-4 text-sm">
            <div className="flex justify-between py-1">
              <span className="text-muted-foreground">Weight in grams</span>
              <span className="font-semibold tabular-nums">{grams.toFixed(4)} g</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-muted-foreground">
                Gold value ({karatLabel(karat)})
              </span>
              <span className="font-semibold tabular-nums">{formatMoney(goldValue, "PKR")}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-muted-foreground">
                + Making charge ({makingPct || 0}%)
              </span>
              <span className="tabular-nums">{formatMoney(makingCharge, "PKR")}</span>
            </div>
            {includeGst && (
              <div className="flex justify-between py-1">
                <span className="text-muted-foreground">+ 17% GST</span>
                <span className="tabular-nums">{formatMoney(gst, "PKR")}</span>
              </div>
            )}
            <div className="mt-2 flex justify-between border-t border-border pt-2 text-lg">
              <span className="font-semibold">Total</span>
              <span className="font-bold text-primary tabular-nums">
                {formatMoney(total, "PKR")}
              </span>
            </div>
          </div>
        </div>

        <article className="prose prose-sm max-w-none mt-8 text-foreground/90">
          <h2 className="text-xl font-bold">
            How Pakistan gold pricing actually works
          </h2>
          <p>
            Pakistan mein jeweller ka final bill teen components ka sum hota hai:{" "}
            <strong>base gold value</strong> (international spot ×
            USD/PKR × purity × weight), <strong>making charge</strong> (labour
            + design margin, 8–20%), aur <strong>GST</strong> (17% on jewellery
            invoices). Ye calculator teenon aapke sample ke against dikhata
            hai taake final quote pehle se estimate ho sake.
          </p>
          <p>
            <strong>Reference rate:</strong> Karachi sarafa bazaar (Bolton
            Market) national benchmark hai. Lahore, Islamabad aur Rawalpindi
            same-day 0.08–0.15% premium par trade karte hain. Chhoti cities
            (Bahawalpur, Sukkur, Larkana) mein 0.15–0.2% zyada rate hota hai
            transport & dealer margin ki wajah se.
          </p>
          <h3 className="text-lg font-bold mt-4">Traditional unit conversions</h3>
          <ul>
            <li>1 Tola = 11.6638 g = 12 Masha = 96 Ratti</li>
            <li>1 Masha ≈ 0.972 g</li>
            <li>1 Ratti ≈ 0.121 g</li>
            <li>1 Bhori = 10 g (Bangladeshi trade unit, occasionally quoted)</li>
          </ul>
        </article>

        <FaqSection faqs={faqs} title="Gold calculator — FAQs" />

        <PakistanNavigator exclude="/pakistan/calculator" />
      </section>
    </Layout>
  );
}